import moment from 'moment';
import { mergeSchedules } from '../../../utils/schedules';
import { isEqual } from 'lodash';
import { convertIntervalToStatus } from './utils';
import * as types from './types';

// This reducer keeps track of two things:
//
// - full: The full schedules per AvailabilityRequirement, as they are currently
// known.
// - currentState: for each AvailabilityRequirement, this contains the state
// that applies to time = now. This is derived from the `intervals` in the
// schedules that are stored in the `full` section of the state. In addition, it
// also keeps track of the previous known state. This is used for example in the
// region map when a state change takes place. By inspecting previous and now,
// the difference in status can be displayed to the user.
export const initialState = {
  loading: false,
  full: {},
  currentState: {
    previous: {},
    now: {}
  }
};

// This function computes the new value of the currentState part of the state.
// It compares the previously known current state against the newly received
// state. If there is a difference, it updates both the "previous" and "new"
// entries of the `currentState` for that particular AvailabilityRequirement id.
const updateCurrentState = (state, id, schedule, timestamp) => {
  if (!schedule.intervals) return state;
  const currentInterval = schedule.intervals.find(interval =>
    // take the interval that applies right now. Start inclusive, end exclusive
    moment().isBetween(moment(interval.start_time), moment(interval.end_time), null, '[)')
  );

  if (!currentInterval) return state;

  const newState = convertIntervalToStatus(currentInterval);

  const previousCurrentState = state.now[id] || {};

  // try to reduce field differences as much as possible by performing a deep
  // equals on the composite fields
  const newCurrentState = {
    availabilityRequirementLevel: isEqual(
      previousCurrentState.availabilityRequirementLevel,
      newState.availabilityRequirementLevel
    )
      ? previousCurrentState.availabilityRequirementLevel
      : newState.availabilityRequirementLevel,
    taskAvailabilityCodes: isEqual(previousCurrentState.taskAvailabilityCodes, newState.taskAvailabilityCodes)
      ? previousCurrentState.taskAvailabilityCodes
      : newState.taskAvailabilityCodes,
    warningLevel: newState.warningLevel,
    serviceLevel: newState.serviceLevel
  };

  // if nothing changed (deep equals), return the current state. This will
  // reduce the number of state updates
  // Ignore the timestamp in the comparison
  const previousCurrentStateWithoutTimestamp = { ...previousCurrentState };
  delete previousCurrentStateWithoutTimestamp.timestamp;
  if (isEqual(previousCurrentStateWithoutTimestamp, newCurrentState)) return state;

  return {
    previous: {
      ...state.previous,
      [id]: state.now[id]
    },
    now: {
      ...state.now,
      [id]: { timestamp, ...newCurrentState }
    }
  };
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    // Received a bulk update, which contains a list of normal
    // AVAILABILITY_REQUIREMENT_SCHEDULE_UPDATED actions. Apply these one by one.
    case types.AVAILABILITY_REQUIREMENT_SCHEDULE_BATCH_UPDATE: {
      return action.payload.actions.reduce((currentState, p) => reducer(currentState, p), state);
    }
    // A new version of the schedule has been received from outside (most
    // likely the server. Store this schedule, and update the currentState
    case types.AVAILABILITY_REQUIREMENT_SCHEDULE_UPDATED: {
      const oldSchedule = state.full[action.payload.id];
      const newSchedule = action.payload.schedule;
      const mergeMode = action.mergeMode;

      return {
        ...state,
        full: {
          ...state.full,
          [action.payload.id]: mergeSchedules(oldSchedule, newSchedule, mergeMode)
        },
        currentState: updateCurrentState(
          state.currentState,
          action.payload.id,
          action.payload.schedule,
          action.timestamp
        )
      };
    }
    // When receiving a schedule tick, it means that the time has progressed with
    // the minimal schedule increment (15 minutes). That means a new currentState
    // could now be in effect. In order to calculdate the new currentState, we
    // offer every schedule in `full` to the updateCurrentState function.
    case 'SCHEDULE_TICK': {
      return {
        ...state,
        currentState: Object.entries(state.full).reduce((currentState, value) => {
          const [id, schedule] = value;
          return updateCurrentState(currentState, id, schedule, action.timestamp);
        }, state.currentState)
      };
    }
    case types.START: {
      return { ...state, loading: true };
    }
    case types.SUCCESS: {
      return { ...state, loading: false };
    }
    default: {
      return state;
    }
  }
};

export default reducer;
